import { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Storage, Url } from '@atlassiansox/bxpkit-core';
import type { AnalyticsTrackType } from '@atlassiansox/bxpkit-core';
import ReactGA from 'react-ga';
import { CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES } from '../../model/check-email-page';
import {
  CartServiceInterface,
  CheckEmailAnalyticsContructorType,
  CheckEmailAnalyticsInterface,
  CheckEmailAnalyticsOptionsType
} from '../../model/check-email-page';

/**
 * Analytics service for Check Email Component
 */

export const CheckEmailAnalytics: CheckEmailAnalyticsContructorType = class CheckEmailAnalyticsClass
  implements CheckEmailAnalyticsInterface
{
  public baseUrl?: string;
  public analyticsTrack?: AnalyticsTrackType;
  public cartService: CartServiceInterface;

  public constructor({
    analyticsTrack,
    cartService,
    googleAnalyticsId = ''
  }: CheckEmailAnalyticsOptionsType) {
    this.analyticsTrack = analyticsTrack;
    this.cartService = cartService;

    ReactGA.initialize(googleAnalyticsId, {
      testMode: process?.env?.NODE_ENV === 'test'
    });
    void this._onloadEvents();
    if (!this.analyticsTrack) {
      // tslint:disable-next-line: no-console
      console.warn('Check Email Component: analytics disabled');
    }
  }

  /**
   * All events that should fire on load
   */

  public async _onloadEvents() {
    const googleApps =
      Url.getParameterByName('googleappsintegration') === 'true';

    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      const products = await this.cartService.getProducts();

      const completedOrderDetails: any = [];

      products.map((product) => {
        // add to cart events
        const detailsAddToCart = {
          id: product.productKey,
          label: `${product.productKey}.cloud`,
          name: product.name,
          description: product.description,
          sku: product.orderableItemId,
          category: 'cart-cloud',
          saleType: product.saleType,
          core_funnel: true
        };
        segment('Added Product', detailsAddToCart);

        // confirmed eval event(s)
        const detailsConfirmedEval = {
          id: product.productKey,
          sku: product.orderableItemId,
          name: product.name,
          description: product.description,
          saleType: 'EVALUATION',
          category: 'cart-cloud',
          core_funnel: false,
          label: `${product.productKey}.cloud`,
          lead_score: '',
          licenses: '',
          platform: 'cloud',
          product: product.name,
          start_date: new Date(),
          _atl_path: Storage.getCookie('__atl_path'),
          _sio: Storage.getCookie('_sio')
        };
        segment('Confirmed Evaluation', detailsConfirmedEval);

        // save for completed order event
        completedOrderDetails.push({
          id: product.productKey,
          sku: product.orderableItemId,
          name: product.name,
          description: product.description,
          saleType: 'EVALUATION'
        });
      });

      // order event with everything in-cart
      if (completedOrderDetails.length > 0) {
        segment('Completed Order', {
          type: 'ONDEMAND_TRIAL',
          googleApps: googleApps.toString(),
          products: completedOrderDetails
        });
      }

      if (googleApps) {
        segment('Confirm Google Apps', {
          category: 'cart-cloud',
          label: 'google-apps',
          core_funnel: false
        });
      }
    }

    ReactGA.event({
      category: 'cart-cloud',
      action: 'Viewed Checkout Step',
      label: 'view-confirmation-page',
      nonInteraction: true
    });

    return Promise.resolve();
  }

  /**
   * User selects resend email button
   */

  public clickResendButton(createAnalyticsEvent: CreateUIAnalyticsEvent) {
    createAnalyticsEvent({
      action: 'clicked',
      name: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.RESEND_EMAIL_CLICKED
    }).fire();

    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      segment('Click resend email', {});
    }

    ReactGA.event({
      category: 'Signup',
      action: 'Clicked',
      label: 'resend-verification-email'
    });
  }

  /**
   * track no-content/204 status
   */

  public resendApiNoContent() {
    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      segment('Resend api returned no content', {});
    }
  }

  /**
   * track already verified/304 status
   */

  public resendApiAlreadyVerified() {
    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      segment('Resend api account already verified', {});
    }
  }

  /**
   * track resend API failures
   */

  public resendApiFailure() {
    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      segment('Resend api fail', {});
    }
  }

  /**
   * track success/200 status
   */

  public resendApiSuccess() {
    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      segment('Resend api success', {});
    }
  }

  /**
   * User selects contact link
   */

  public contactLinkAnalytics(createAnalyticsEvent: CreateUIAnalyticsEvent) {
    createAnalyticsEvent({
      action: 'clicked',
      name: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.CONTACT_LINK_CLICKED
    }).fire();

    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      segment('Click contact us', {});
    }
  }

  /**
   * User selects create a new account link
   */

  public navigateBack(
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    createAnalyticsEvent: CreateUIAnalyticsEvent
  ) {
    event.preventDefault();

    createAnalyticsEvent({
      action: 'clicked',
      name: CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES.CREATE_NEW_ACCOUNT_CLICKED
    }).fire();

    if (this.analyticsTrack) {
      const { segment } = this.analyticsTrack;
      segment('Click change email', {});
    }

    window.history.back();
  }
};

declare global {
  interface Window {
    ga: any;
  }
}
